import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Plus,
  X,
  Eye,
  RefreshCw,
  Receipt,
  FileText,
  CreditCard,
  Wallet,
  MessageCircle,
  Printer,
  Download,
  User,
  Phone,
  Mail,
  MapPin,
  IndianRupee,
  ArrowUpRight,
  ArrowDownRight,
  History,
  ChevronRight,
  Building2,
  Banknote,
  CheckCircle,
  AlertCircle,
  FilePlus2,
} from "lucide-react";

import api from "../constant/api";

export default function CustomerMangement() {
  /* =========================================================
     CUSTOMER LIST
  ========================================================= */

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all");

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);

  /* =========================================================
     CUSTOMER FORM
  ========================================================= */

  const emptyCustomer = {
    name: "",
    phone: "",
    whatsapp_no: "",
    email: "",
    city: "",
    address: "",
    gst_no: "",
    opening_balance: "0",
    credit_limit: "0",
    status: "Active",
  };

  const [customerForm, setCustomerForm] =
    useState(emptyCustomer);

  /* =========================================================
     PAYMENT / RECEIPT FORM
  ========================================================= */

  const emptyPayment = {
    invoice_id: "",
    order_id: "",
    payment_method: "Cash",
    amount: "",
    transaction_no: "",
    bank_name: "",
    cheque_no: "",
    notes: "",
  };

  const [paymentForm, setPaymentForm] =
    useState(emptyPayment);

  /* =========================================================
     CREDIT NOTE FORM
  ========================================================= */

  const emptyCredit = {
    invoice_id: "",
    order_id: "",
    amount: "",
    reason: "",
  };

  const [creditForm, setCreditForm] =
    useState(emptyCredit);

  /* =========================================================
     TAB
  ========================================================= */

  const [tab, setTab] = useState("summary");

  /* =========================================================
     LOAD CUSTOMERS
  ========================================================= */

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        "/customer/getAllCustomers"
      );

      if (res.data?.success) {
        setCustomers(res.data.data || []);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error(
        "Customer loading error:",
        error
      );

      alert(
        error?.response?.data?.message ||
          "Failed to load customers"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     OPEN CUSTOMER
  ========================================================= */

  const openCustomer = async (customerId) => {
    try {
      setLoadingDetails(true);

      const res = await api.get(
        `/customer/getCustomerDetails/${customerId}`
      );

      if (res.data?.success) {
        setSelectedCustomer(res.data);
        setTab("summary");
      } else {
        alert(
          res.data?.message ||
            "Customer details not found"
        );
      }
    } catch (error) {
      console.error(
        "Customer detail error:",
        error
      );

      alert(
        error?.response?.data?.message ||
          "Failed to load customer details"
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  /* =========================================================
     CLOSE CUSTOMER
  ========================================================= */

  const closeCustomer = () => {
    setSelectedCustomer(null);
    setTab("summary");
  };

  /* =========================================================
     MONEY
  ========================================================= */

  const money = (value) => {
    return `₹${Number(value || 0).toLocaleString(
      "en-IN",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    )}`;
  };

  /* =========================================================
     FILTER CUSTOMERS
  ========================================================= */

  const filteredCustomers = useMemo(() => {
    const text =
      search.trim().toLowerCase();

    return customers.filter((customer) => {
      const name =
        String(
          customer.name || ""
        ).toLowerCase();

      const phone =
        String(
          customer.phone || ""
        ).toLowerCase();

      const whatsapp =
        String(
          customer.whatsapp_no || ""
        ).toLowerCase();

      const matchesSearch =
        !text ||
        name.includes(text) ||
        phone.includes(text) ||
        whatsapp.includes(text);

      const balance = Number(
        customer.outstanding_balance || 0
      );

      let matchesBalance = true;

      if (balanceFilter === "due") {
        matchesBalance = balance > 0;
      }

      if (balanceFilter === "credit") {
        matchesBalance = balance < 0;
      }

      if (balanceFilter === "clear") {
        matchesBalance = balance === 0;
      }

      return (
        matchesSearch &&
        matchesBalance
      );
    });
  }, [
    customers,
    search,
    balanceFilter,
  ]);

  /* =========================================================
     CUSTOMER SUMMARY
  ========================================================= */

  const summary =
    selectedCustomer?.summary || {};

  const customer =
    selectedCustomer?.customer || {};

  const orders =
    selectedCustomer?.orders || [];

  const invoices =
    selectedCustomer?.invoices || [];

  const receipts =
    selectedCustomer?.receipts || [];

  const creditNotes =
    selectedCustomer?.creditNotes ||
    selectedCustomer?.credit_notes ||
    [];

  const ledger =
    selectedCustomer?.ledger || [];

  /* =========================================================
     ADD CUSTOMER
  ========================================================= */

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;

    setCustomerForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitCustomer = async (e) => {
    e.preventDefault();

    if (
      !customerForm.name.trim()
    ) {
      alert("Customer name is required");
      return;
    }

    try {
      const res = await api.post(
        "/customer/createCustomer",
        {
          ...customerForm,
          opening_balance:
            Number(
              customerForm.opening_balance || 0
            ),
          credit_limit:
            Number(
              customerForm.credit_limit || 0
            ),
        }
      );

      if (res.data?.success) {
        alert(
          res.data.message ||
            "Customer created successfully"
        );

        setShowCustomerModal(false);
        setCustomerForm(emptyCustomer);

        await loadCustomers();
      } else {
        alert(
          res.data?.message ||
            "Customer creation failed"
        );
      }
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data?.message ||
          "Customer creation failed"
      );
    }
  };

  /* =========================================================
     OPEN PAYMENT MODAL
  ========================================================= */

  const openPaymentModal = () => {
    setPaymentForm({
      ...emptyPayment,
    });

    setShowPaymentModal(true);
  };

  /* =========================================================
     PAYMENT CHANGE
  ========================================================= */

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;

    setPaymentForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     ADD PAYMENT / RECEIPT
  ========================================================= */

  const submitPayment = async (e) => {
    e.preventDefault();

    if (!selectedCustomer?.customer?.customer_id) {
      alert("Customer not selected");
      return;
    }

    if (
      !paymentForm.amount ||
      Number(paymentForm.amount) <= 0
    ) {
      alert("Enter a valid payment amount");
      return;
    }

    try {
      const payload = {
        customer_id:
          selectedCustomer.customer.customer_id,

        invoice_id:
          paymentForm.invoice_id
            ? Number(paymentForm.invoice_id)
            : null,

        order_id:
          paymentForm.order_id
            ? Number(paymentForm.order_id)
            : null,

        payment_method:
          paymentForm.payment_method ||
          "Cash",

        amount:
          Number(paymentForm.amount),

        transaction_no:
          paymentForm.transaction_no ||
          null,

        bank_name:
          paymentForm.bank_name ||
          null,

        cheque_no:
          paymentForm.cheque_no ||
          null,

        notes:
          paymentForm.notes ||
          null,
      };

      /*
       * Backend supports /addPayment.
       */

      const res = await api.post(
        "/customer/addPayment",
        payload
      );

      if (res.data?.success) {
        alert(
          `${
            res.data.receipt_no ||
            "Receipt"
          } created successfully`
        );

        setShowPaymentModal(false);
        setPaymentForm({
          ...emptyPayment,
        });

        await openCustomer(
          selectedCustomer.customer.customer_id
        );

        await loadCustomers();
      } else {
        alert(
          res.data?.message ||
            "Payment failed"
        );
      }
    } catch (error) {
      console.error(
        "Payment error:",
        error
      );

      alert(
        error?.response?.data?.message ||
          "Payment / receipt creation failed"
      );
    }
  };

  /* =========================================================
     OPEN CREDIT NOTE
  ========================================================= */

  const openCreditNoteModal = () => {
    setCreditForm({
      ...emptyCredit,
    });

    setShowCreditModal(true);
  };

  /* =========================================================
     CREDIT CHANGE
  ========================================================= */

  const handleCreditChange = (e) => {
    const { name, value } = e.target;

    setCreditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     ADD CREDIT NOTE
  ========================================================= */

  const submitCreditNote = async (e) => {
    e.preventDefault();

    if (!selectedCustomer?.customer?.customer_id) {
      alert("Customer not selected");
      return;
    }

    if (
      !creditForm.amount ||
      Number(creditForm.amount) <= 0
    ) {
      alert(
        "Enter a valid credit note amount"
      );
      return;
    }

    if (
      !creditForm.reason.trim()
    ) {
      alert(
        "Please enter credit note reason"
      );
      return;
    }

    try {
      const payload = {
        customer_id:
          selectedCustomer.customer.customer_id,

        invoice_id:
          creditForm.invoice_id
            ? Number(creditForm.invoice_id)
            : null,

        order_id:
          creditForm.order_id
            ? Number(creditForm.order_id)
            : null,

        amount:
          Number(creditForm.amount),

        reason:
          creditForm.reason.trim(),
      };

      const res = await api.post(
        "/customer/addCreditNote",
        payload
      );

      if (res.data?.success) {
        alert(
          `${
            res.data.credit_note_no ||
            "Credit Note"
          } created successfully`
        );

        setShowCreditModal(false);
        setCreditForm({
          ...emptyCredit,
        });

        await openCustomer(
          selectedCustomer.customer.customer_id
        );

        await loadCustomers();
      } else {
        alert(
          res.data?.message ||
            "Credit note failed"
        );
      }
    } catch (error) {
      console.error(
        "Credit note error:",
        error
      );

      alert(
        error?.response?.data?.message ||
          "Credit note creation failed"
      );
    }
  };

  /* =========================================================
     WHATSAPP
  ========================================================= */

  const sendWhatsApp = async () => {
    if (
      !selectedCustomer?.customer
        ?.customer_id
    ) {
      return;
    }

    try {
      const id =
        selectedCustomer.customer.customer_id;

      const res = await api.get(
        `/customer/whatsappBalance/${id}`
      );

      if (
        res.data?.whatsapp_url
      ) {
        window.open(
          res.data.whatsapp_url,
          "_blank"
        );
      } else {
        alert(
          "WhatsApp URL not available"
        );
      }
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data?.message ||
          "WhatsApp message failed"
      );
    }
  };

  /* =========================================================
     PRINT RECEIPT
  ========================================================= */

  const printReceipt = async (
    paymentId
  ) => {
    try {
      const res = await api.get(
        `/customer/getReceipt/${paymentId}`
      );

      if (
        !res.data?.success ||
        !res.data?.receipt
      ) {
        alert("Receipt not found");
        return;
      }

      const receipt =
        res.data.receipt;

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${receipt.receipt_no || "Receipt"}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 30px;
              color: #111;
            }

            .receipt {
              max-width: 700px;
              margin: auto;
              border: 1px solid #ddd;
              padding: 30px;
            }

            h1 {
              margin: 0 0 5px;
              text-align: center;
            }

            .center {
              text-align: center;
            }

            .row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }

            .amount {
              font-size: 28px;
              font-weight: bold;
            }

            .footer {
              margin-top: 30px;
              text-align: center;
              color: #777;
              font-size: 12px;
            }

            @media print {
              body {
                padding: 0;
              }

              .receipt {
                border: none;
              }
            }
          </style>
        </head>

        <body>
          <div class="receipt">

            <h1>PAYMENT RECEIPT</h1>

            <div class="center">
              <strong>
                ${receipt.receipt_no || "-"}
              </strong>
            </div>

            <br />

            <div class="row">
              <span>Customer</span>
              <strong>
                ${receipt.name || "-"}
              </strong>
            </div>

            <div class="row">
              <span>Phone</span>
              <strong>
                ${receipt.phone || "-"}
              </strong>
            </div>

            <div class="row">
              <span>Invoice</span>
              <strong>
                ${receipt.invoice_no || "-"}
              </strong>
            </div>

            <div class="row">
              <span>Payment Method</span>
              <strong>
                ${receipt.payment_method || "-"}
              </strong>
            </div>

            ${
              receipt.transaction_no
                ? `
              <div class="row">
                <span>Transaction No</span>
                <strong>
                  ${receipt.transaction_no}
                </strong>
              </div>
            `
                : ""
            }

            ${
              receipt.bank_name
                ? `
              <div class="row">
                <span>Bank</span>
                <strong>
                  ${receipt.bank_name}
                </strong>
              </div>
            `
                : ""
            }

            ${
              receipt.cheque_no
                ? `
              <div class="row">
                <span>Cheque No</span>
                <strong>
                  ${receipt.cheque_no}
                </strong>
              </div>
            `
                : ""
            }

            <br />

            <div class="center">
              <div>Received Amount</div>
              <div class="amount">
                ${money(receipt.amount)}
              </div>
            </div>

            ${
              receipt.notes
                ? `
              <br />
              <div>
                <strong>Notes:</strong>
                ${receipt.notes}
              </div>
            `
                : ""
            }

            <div class="footer">
              Thank you
            </div>

          </div>
        </body>
        </html>
      `;

      const win =
        window.open(
          "",
          "_blank",
          "width=800,height=900"
        );

      if (!win) {
        alert(
          "Please allow popup to print receipt"
        );
        return;
      }

      win.document.write(html);
      win.document.close();

      setTimeout(() => {
        win.focus();
        win.print();
      }, 400);
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data?.message ||
          "Failed to print receipt"
      );
    }
  };

  /* =========================================================
     PRINT CREDIT NOTE
  ========================================================= */

  const printCreditNote = (note) => {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>
          ${note.credit_note_no || "Credit Note"}
        </title>

        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
          }

          .box {
            max-width: 700px;
            margin: auto;
            border: 1px solid #ddd;
            padding: 30px;
          }

          h1 {
            text-align: center;
            margin-bottom: 5px;
          }

          .center {
            text-align: center;
          }

          .row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px solid #eee;
          }

          .amount {
            font-size: 28px;
            font-weight: bold;
          }

          .footer {
            margin-top: 40px;
            text-align: center;
            color: #777;
          }

          @media print {
            body {
              padding: 0;
            }

            .box {
              border: none;
            }
          }
        </style>
      </head>

      <body>

        <div class="box">

          <h1>CREDIT NOTE</h1>

          <div class="center">
            <strong>
              ${note.credit_note_no || "-"}
            </strong>
          </div>

          <br />

          <div class="row">
            <span>Customer</span>
            <strong>
              ${customer.name || "-"}
            </strong>
          </div>

          <div class="row">
            <span>Invoice</span>
            <strong>
              ${note.invoice_no || "-"}
            </strong>
          </div>

          <div class="row">
            <span>Date</span>
            <strong>
              ${
                note.credit_date ||
                note.created_at ||
                "-"
              }
            </strong>
          </div>

          <br />

          <div class="center">
            <div>Credit Amount</div>

            <div class="amount">
              ${money(note.amount)}
            </div>
          </div>

          <br />

          <div>
            <strong>Reason</strong>

            <p>
              ${note.reason || "-"}
            </p>
          </div>

          <div class="footer">
            This is a computer generated credit note.
          </div>

        </div>

      </body>
      </html>
    `;

    const win =
      window.open(
        "",
        "_blank",
        "width=800,height=900"
      );

    if (!win) {
      alert(
        "Please allow popup to print credit note"
      );
      return;
    }

    win.document.write(html);
    win.document.close();

    setTimeout(() => {
      win.focus();
      win.print();
    }, 400);
  };

  /* =========================================================
     EXPORT CSV
  ========================================================= */

  const exportCustomers = () => {
    if (!filteredCustomers.length) {
      alert("No customers to export");
      return;
    }

    const headers = [
      "Customer ID",
      "Customer Name",
      "Phone",
      "WhatsApp",
      "Email",
      "City",
      "GST",
      "Opening Balance",
      "Invoice Total",
      "Paid",
      "Credit Note",
      "Outstanding",
      "Status",
    ];

    const rows =
      filteredCustomers.map(
        (c) => [
          c.customer_id || "",
          c.name || "",
          c.phone || "",
          c.whatsapp_no || "",
          c.email || "",
          c.city || "",
          c.gst_no || "",
          c.opening_balance || 0,
          c.total_invoice_amount || 0,
          c.total_paid || 0,
          c.total_credit_note || 0,
          c.outstanding_balance || 0,
          c.status || "",
        ]
      );

    const csv = [
      headers,
      ...rows,
    ]
      .map((row) =>
        row
          .map((value) =>
            `"${String(value).replace(
              /"/g,
              '""'
            )}"`
          )
          .join(",")
      )
      .join("\n");

    const blob =
      new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;
    link.download =
      `customers_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;

    link.click();

    URL.revokeObjectURL(url);
  };

  /* =========================================================
     TRANSACTION LIST
  ========================================================= */

  const renderTransactionList = (
    items,
    type
  ) => {
    if (!items.length) {
      return (
        <div className="py-12 text-center">
          <FileText
            size={40}
            className="mx-auto text-slate-300"
          />

          <p className="mt-3 font-bold text-slate-500">
            No {type} records found
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {items.map(
          (item, index) => {
            const amount = Number(
              item.amount ??
                item.total ??
                item.net_total ??
                item.total_amount ??
                0
            );

            const key =
              item.payment_id ??
              item.credit_note_id ??
              item.invoice_id ??
              item.order_id ??
              index;

            const title =
              item.receipt_no ||
              item.credit_note_no ||
              item.invoice_no ||
              item.order_no ||
              `#${key}`;

            const date =
              item.payment_date ||
              item.credit_date ||
              item.created_at ||
              item.invoice_date ||
              item.order_date ||
              item.date ||
              "-";

            return (
              <div
                key={key}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">

                  <div className="flex items-start gap-3">

                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                        type === "receipt"
                          ? "bg-emerald-100 text-emerald-600"
                          : type === "credit"
                          ? "bg-orange-100 text-orange-600"
                          : type === "invoice"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {type === "receipt" ? (
                        <Receipt size={21} />
                      ) : type === "credit" ? (
                        <CreditCard size={21} />
                      ) : (
                        <FileText size={21} />
                      )}
                    </div>

                    <div>
                      <p className="font-black text-slate-800">
                        {title}
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        {date}
                      </p>

                      {item.payment_method && (
                        <p className="mt-1 text-xs font-semibold text-slate-500">
                          Method:{" "}
                          {item.payment_method}
                        </p>
                      )}

                      {item.reason && (
                        <p className="mt-1 text-xs text-slate-500">
                          Reason:{" "}
                          {item.reason}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 md:justify-end">

                    <div className="text-right">
                      <p
                        className={`text-lg font-black ${
                          type === "credit"
                            ? "text-orange-600"
                            : type === "receipt"
                            ? "text-emerald-600"
                            : "text-slate-900"
                        }`}
                      >
                        {money(amount)}
                      </p>
                    </div>

                    {type === "receipt" &&
                      item.payment_id && (
                        <button
                          onClick={() =>
                            printReceipt(
                              item.payment_id
                            )
                          }
                          className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white hover:bg-slate-700"
                          title="Print Receipt"
                        >
                          <Printer size={17} />
                        </button>
                      )}

                    {type === "credit" && (
                      <button
                        onClick={() =>
                          printCreditNote(item)
                        }
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-white hover:bg-orange-600"
                        title="Print Credit Note"
                      >
                        <Printer size={17} />
                      </button>
                    )}
                  </div>

                </div>
              </div>
            );
          }
        )}
      </div>
    );
  };

  /* =========================================================
     CUSTOMER DETAIL DRAWER
  ========================================================= */

  const CustomerDrawer = () => {
    if (!selectedCustomer) {
      return null;
    }

    return (
      <div className="fixed inset-0 z-50 flex">

        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={closeCustomer}
        />

        <div className="relative ml-auto flex h-full w-full max-w-5xl flex-col overflow-hidden bg-slate-50 shadow-2xl">

          {/* HEADER */}

          <div className="sticky top-0 z-20 border-b bg-white px-4 py-4 md:px-6">

            <div className="flex items-start justify-between gap-4">

              <div className="flex min-w-0 items-center gap-3">

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white">
                  <User size={22} />
                </div>

                <div className="min-w-0">
                  <h2 className="truncate text-xl font-black text-slate-900">
                    {customer.name}
                  </h2>

                  <p className="text-sm text-slate-500">
                    {customer.phone ||
                      "No phone"}
                  </p>
                </div>

              </div>

              <button
                onClick={closeCustomer}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200"
              >
                <X size={20} />
              </button>

            </div>

            {/* ACTIONS */}

            <div className="mt-4 grid grid-cols-2 gap-2 md:flex">

              <button
                onClick={openPaymentModal}
                className="flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white hover:bg-emerald-700"
              >
                <Receipt size={17} />
                Add Receipt
              </button>

              <button
                onClick={openPaymentModal}
                className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white hover:bg-blue-700"
              >
                <Wallet size={17} />
                Add Payment
              </button>

              <button
                onClick={openCreditNoteModal}
                className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-black text-white hover:bg-orange-600"
              >
                <CreditCard size={17} />
                Credit Note
              </button>

              <button
                onClick={sendWhatsApp}
                className="flex items-center justify-center gap-2 rounded-xl bg-green-500 px-4 py-3 text-sm font-black text-white hover:bg-green-600"
              >
                <MessageCircle size={17} />
                WhatsApp
              </button>

            </div>

          </div>

          {/* CONTENT */}

          <div className="flex-1 overflow-y-auto p-4 md:p-6">

            {/* SUMMARY CARDS */}

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">

              <SummaryCard
                title="Invoice"
                value={money(
                  summary.total_invoice_amount ??
                    customer.total_invoice_amount
                )}
                icon={
                  <FileText size={19} />
                }
              />

              <SummaryCard
                title="Paid"
                value={money(
                  summary.total_paid ??
                    customer.total_paid
                )}
                icon={
                  <ArrowDownRight
                    size={19}
                  />
                }
                green
              />

              <SummaryCard
                title="Credit Notes"
                value={money(
                  summary.total_credit_note ??
                    customer.total_credit_note
                )}
                icon={
                  <CreditCard size={19} />
                }
                orange
              />

              <SummaryCard
                title="Outstanding"
                value={money(
                  summary.outstanding_balance ??
                    customer.outstanding_balance
                )}
                icon={
                  <IndianRupee size={19} />
                }
                red={
                  Number(
                    summary.outstanding_balance ??
                      customer.outstanding_balance ??
                      0
                  ) > 0
                }
              />

            </div>

            {/* CUSTOMER INFO */}

            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

                <InfoItem
                  icon={<Phone size={16} />}
                  label="Phone"
                  value={
                    customer.phone || "-"
                  }
                />

                <InfoItem
                  icon={<MessageCircle size={16} />}
                  label="WhatsApp"
                  value={
                    customer.whatsapp_no ||
                    customer.phone ||
                    "-"
                  }
                />

                <InfoItem
                  icon={<Mail size={16} />}
                  label="Email"
                  value={
                    customer.email || "-"
                  }
                />

                <InfoItem
                  icon={<MapPin size={16} />}
                  label="City"
                  value={
                    customer.city || "-"
                  }
                />

                <InfoItem
                  icon={<Building2 size={16} />}
                  label="GST"
                  value={
                    customer.gst_no || "-"
                  }
                />

                <InfoItem
                  icon={<Wallet size={16} />}
                  label="Credit Limit"
                  value={money(
                    customer.credit_limit
                  )}
                />

              </div>

              {customer.address && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Address
                  </p>

                  <p className="mt-1 text-sm font-semibold text-slate-700">
                    {customer.address}
                  </p>
                </div>
              )}

            </div>

            {/* TABS */}

            <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2">

              <div className="flex min-w-max gap-2">

                <TabButton
                  active={
                    tab === "summary"
                  }
                  onClick={() =>
                    setTab("summary")
                  }
                  icon={<History size={16} />}
                  label="Summary"
                />

                <TabButton
                  active={
                    tab === "orders"
                  }
                  onClick={() =>
                    setTab("orders")
                  }
                  icon={<FileText size={16} />}
                  label={`Orders (${orders.length})`}
                />

                <TabButton
                  active={
                    tab === "invoice"
                  }
                  onClick={() =>
                    setTab("invoice")
                  }
                  icon={<FileText size={16} />}
                  label={`Invoices (${invoices.length})`}
                />

                <TabButton
                  active={
                    tab === "receipt"
                  }
                  onClick={() =>
                    setTab("receipt")
                  }
                  icon={<Receipt size={16} />}
                  label={`Receipts (${receipts.length})`}
                />

                <TabButton
                  active={
                    tab === "credit"
                  }
                  onClick={() =>
                    setTab("credit")
                  }
                  icon={<CreditCard size={16} />}
                  label={`Credit Notes (${creditNotes.length})`}
                />

                <TabButton
                  active={
                    tab === "ledger"
                  }
                  onClick={() =>
                    setTab("ledger")
                  }
                  icon={<History size={16} />}
                  label={`Ledger (${ledger.length})`}
                />

              </div>

            </div>

            {/* TAB CONTENT */}

            <div className="mt-4">

              {tab === "summary" && (
                <div className="space-y-4">

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">

                    <h3 className="text-lg font-black text-slate-900">
                      Account Summary
                    </h3>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">

                      <MiniStat
                        label="Opening Balance"
                        value={money(
                          customer.opening_balance
                        )}
                      />

                      <MiniStat
                        label="Total Invoice"
                        value={money(
                          summary.total_invoice_amount ??
                            customer.total_invoice_amount
                        )}
                      />

                      <MiniStat
                        label="Total Paid"
                        value={money(
                          summary.total_paid ??
                            customer.total_paid
                        )}
                      />

                      <MiniStat
                        label="Credit Notes"
                        value={money(
                          summary.total_credit_note ??
                            customer.total_credit_note
                        )}
                      />

                    </div>

                  </div>

                  {ledger.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">

                      <h3 className="text-lg font-black text-slate-900">
                        Recent Ledger
                      </h3>

                      <div className="mt-4 space-y-2">
                        {ledger
                          .slice(0, 5)
                          .map(
                            (item, index) => (
                              <LedgerRow
                                key={
                                  item.ledger_id ??
                                  index
                                }
                                item={item}
                                money={money}
                              />
                            )
                          )}
                      </div>

                    </div>
                  )}

                </div>
              )}

              {tab === "orders" &&
                renderTransactionList(
                  orders,
                  "order"
                )}

              {tab === "invoice" &&
                renderTransactionList(
                  invoices,
                  "invoice"
                )}

              {tab === "receipt" &&
                renderTransactionList(
                  receipts,
                  "receipt"
                )}

              {tab === "credit" &&
                renderTransactionList(
                  creditNotes,
                  "credit note"
                )}

              {tab === "ledger" && (
                <div className="space-y-2">
                  {ledger.length ? (
                    ledger.map(
                      (item, index) => (
                        <LedgerRow
                          key={
                            item.ledger_id ??
                            index
                          }
                          item={item}
                          money={money}
                        />
                      )
                    )
                  ) : (
                    <EmptyState
                      title="No ledger records"
                    />
                  )}
                </div>
              )}

            </div>

          </div>

        </div>

      </div>
    );
  };

  /* =========================================================
     PAYMENT MODAL
  ========================================================= */

  const PaymentModal = () => {
    if (!showPaymentModal) {
      return null;
    }

    return (
      <Modal
        title="Add Receipt / Payment"
        onClose={() =>
          setShowPaymentModal(false)
        }
      >

        <form
          onSubmit={submitPayment}
          className="space-y-4"
        >

          <div className="rounded-xl bg-emerald-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-600">
              Customer
            </p>

            <p className="mt-1 text-lg font-black text-slate-900">
              {customer.name}
            </p>

            <p className="text-sm text-slate-500">
              Outstanding:{" "}
              {money(
                customer.outstanding_balance
              )}
            </p>
          </div>

          {/* INVOICE */}

          <SelectField
            label="Invoice"
            name="invoice_id"
            value={
              paymentForm.invoice_id
            }
            onChange={
              handlePaymentChange
            }
          >
            <option value="">
              General Customer Payment
            </option>

            {invoices.map((invoice) => (
              <option
                key={invoice.invoice_id}
                value={invoice.invoice_id}
              >
                {invoice.invoice_no ||
                  `Invoice #${invoice.invoice_id}`}{" "}
                —{" "}
                {money(
                  invoice.balance_amount ??
                    invoice.net_total
                )}
              </option>
            ))}
          </SelectField>

          {/* ORDER */}

          <SelectField
            label="Order"
            name="order_id"
            value={
              paymentForm.order_id
            }
            onChange={
              handlePaymentChange
            }
          >
            <option value="">
              No Order
            </option>

            {orders.map((order) => (
              <option
                key={order.order_id}
                value={order.order_id}
              >
                {order.order_no ||
                  `Order #${order.order_id}`}
              </option>
            ))}
          </SelectField>

          {/* METHOD */}

          <SelectField
            label="Payment Method"
            name="payment_method"
            value={
              paymentForm.payment_method
            }
            onChange={
              handlePaymentChange
            }
          >
            <option value="Cash">
              Cash
            </option>

            <option value="UPI">
              UPI
            </option>

            <option value="Bank">
              Bank Transfer
            </option>

            <option value="Card">
              Card
            </option>

            <option value="Cheque">
              Cheque
            </option>

            <option value="Other">
              Other
            </option>
          </SelectField>

          {/* AMOUNT */}

          <InputField
            label="Amount *"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={
              paymentForm.amount
            }
            onChange={
              handlePaymentChange
            }
            placeholder="0.00"
          />

          {/* TRANSACTION */}

          <InputField
            label="Transaction No"
            name="transaction_no"
            value={
              paymentForm.transaction_no
            }
            onChange={
              handlePaymentChange
            }
            placeholder="UPI / Bank transaction number"
          />

          {/* BANK */}

          {(paymentForm.payment_method ===
            "Bank" ||
            paymentForm.payment_method ===
              "Cheque") && (
            <InputField
              label="Bank Name"
              name="bank_name"
              value={
                paymentForm.bank_name
              }
              onChange={
                handlePaymentChange
              }
              placeholder="Bank name"
            />
          )}

          {/* CHEQUE */}

          {paymentForm.payment_method ===
            "Cheque" && (
            <InputField
              label="Cheque No"
              name="cheque_no"
              value={
                paymentForm.cheque_no
              }
              onChange={
                handlePaymentChange
              }
              placeholder="Cheque number"
            />
          )}

          {/* NOTES */}

          <TextAreaField
            label="Notes"
            name="notes"
            value={
              paymentForm.notes
            }
            onChange={
              handlePaymentChange
            }
            placeholder="Payment notes..."
          />

          <div className="flex gap-3 pt-2">

            <button
              type="button"
              onClick={() =>
                setShowPaymentModal(false)
              }
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold text-slate-700"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 font-black text-white hover:bg-emerald-700"
            >
              <Receipt size={18} />
              Save Receipt
            </button>

          </div>

        </form>

      </Modal>
    );
  };

  /* =========================================================
     CREDIT NOTE MODAL
  ========================================================= */

  const CreditModal = () => {
    if (!showCreditModal) {
      return null;
    }

    return (
      <Modal
        title="Create Credit Note"
        onClose={() =>
          setShowCreditModal(false)
        }
      >

        <form
          onSubmit={
            submitCreditNote
          }
          className="space-y-4"
        >

          <div className="rounded-xl bg-orange-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-orange-600">
              Customer
            </p>

            <p className="mt-1 text-lg font-black">
              {customer.name}
            </p>
          </div>

          <SelectField
            label="Invoice"
            name="invoice_id"
            value={
              creditForm.invoice_id
            }
            onChange={
              handleCreditChange
            }
          >
            <option value="">
              General Credit Note
            </option>

            {invoices.map((invoice) => (
              <option
                key={invoice.invoice_id}
                value={invoice.invoice_id}
              >
                {invoice.invoice_no ||
                  `Invoice #${invoice.invoice_id}`}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Order"
            name="order_id"
            value={
              creditForm.order_id
            }
            onChange={
              handleCreditChange
            }
          >
            <option value="">
              No Order
            </option>

            {orders.map((order) => (
              <option
                key={order.order_id}
                value={order.order_id}
              >
                {order.order_no ||
                  `Order #${order.order_id}`}
              </option>
            ))}
          </SelectField>

          <InputField
            label="Credit Amount *"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={
              creditForm.amount
            }
            onChange={
              handleCreditChange
            }
            placeholder="0.00"
          />

          <TextAreaField
            label="Reason *"
            name="reason"
            value={
              creditForm.reason
            }
            onChange={
              handleCreditChange
            }
            placeholder="Enter credit note reason..."
          />

          <div className="rounded-xl bg-orange-50 p-3 text-sm text-orange-700">
            Credit note amount will reduce the customer's outstanding balance.
          </div>

          <div className="flex gap-3 pt-2">

            <button
              type="button"
              onClick={() =>
                setShowCreditModal(false)
              }
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 font-black text-white hover:bg-orange-600"
            >
              <CreditCard size={18} />
              Create Credit Note
            </button>

          </div>

        </form>

      </Modal>
    );
  };

  /* =========================================================
     ADD CUSTOMER MODAL
  ========================================================= */

  const CustomerModal = () => {
    if (!showCustomerModal) {
      return null;
    }

    return (
      <Modal
        title="Add Customer"
        onClose={() =>
          setShowCustomerModal(false)
        }
      >

        <form
          onSubmit={submitCustomer}
          className="space-y-4"
        >

          <InputField
            label="Customer Name *"
            name="name"
            value={
              customerForm.name
            }
            onChange={
              handleCustomerChange
            }
            placeholder="Customer name"
          />

          <div className="grid gap-4 md:grid-cols-2">

            <InputField
              label="Phone"
              name="phone"
              value={
                customerForm.phone
              }
              onChange={
                handleCustomerChange
              }
              placeholder="Phone"
            />

            <InputField
              label="WhatsApp"
              name="whatsapp_no"
              value={
                customerForm.whatsapp_no
              }
              onChange={
                handleCustomerChange
              }
              placeholder="WhatsApp number"
            />

          </div>

          <InputField
            label="Email"
            name="email"
            type="email"
            value={
              customerForm.email
            }
            onChange={
              handleCustomerChange
            }
            placeholder="Email"
          />

          <div className="grid gap-4 md:grid-cols-2">

            <InputField
              label="City"
              name="city"
              value={
                customerForm.city
              }
              onChange={
                handleCustomerChange
              }
              placeholder="City"
            />

            <InputField
              label="GST No"
              name="gst_no"
              value={
                customerForm.gst_no
              }
              onChange={
                handleCustomerChange
              }
              placeholder="GST number"
            />

          </div>

          <TextAreaField
            label="Address"
            name="address"
            value={
              customerForm.address
            }
            onChange={
              handleCustomerChange
            }
            placeholder="Customer address"
          />

          <div className="grid gap-4 md:grid-cols-2">

            <InputField
              label="Opening Balance"
              name="opening_balance"
              type="number"
              step="0.01"
              value={
                customerForm.opening_balance
              }
              onChange={
                handleCustomerChange
              }
            />

            <InputField
              label="Credit Limit"
              name="credit_limit"
              type="number"
              step="0.01"
              value={
                customerForm.credit_limit
              }
              onChange={
                handleCustomerChange
              }
            />

          </div>

          <SelectField
            label="Status"
            name="status"
            value={
              customerForm.status
            }
            onChange={
              handleCustomerChange
            }
          >
            <option value="Active">
              Active
            </option>

            <option value="Inactive">
              Inactive
            </option>
          </SelectField>

          <div className="flex gap-3 pt-2">

            <button
              type="button"
              onClick={() =>
                setShowCustomerModal(false)
              }
              className="flex-1 rounded-xl border border-slate-200 px-4 py-3 font-bold"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 font-black text-white"
            >
              <Plus size={18} />
              Create Customer
            </button>

          </div>

        </form>

      </Modal>
    );
  };

  /* =========================================================
     MAIN UI
  ========================================================= */

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">

      {/* HEADER */}

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

        <div>
          <h1 className="text-2xl font-black text-slate-900 md:text-3xl">
            Customer Management
          </h1>

          <p className="mt-1 text-sm text-slate-500">
            Customers • Orders • Invoices • Receipts • Payments • Credit Notes
          </p>
        </div>

        <div className="flex flex-wrap gap-2">

          <button
            onClick={loadCustomers}
            disabled={loading}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <RefreshCw
              size={17}
              className={
                loading
                  ? "animate-spin"
                  : ""
              }
            />

            Refresh
          </button>

          <button
            onClick={exportCustomers}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <Download size={17} />
            Export
          </button>

          <button
            onClick={() => {
              setCustomerForm({
                ...emptyCustomer,
              });

              setShowCustomerModal(
                true
              );
            }}
            className="flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 font-black text-white shadow-lg hover:bg-slate-800"
          >
            <Plus size={18} />
            Add Customer
          </button>

        </div>

      </div>

      {/* SEARCH / FILTER */}

      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">

        <div className="flex flex-col gap-3 lg:flex-row">

          <div className="relative flex-1">

            <Search
              size={19}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              placeholder="Search customer / phone / WhatsApp..."
              className="w-full rounded-xl border border-slate-200 py-3 pl-11 pr-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />

          </div>

          <select
            value={balanceFilter}
            onChange={(e) =>
              setBalanceFilter(
                e.target.value
              )
            }
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 font-bold outline-none"
          >
            <option value="all">
              All Customers
            </option>

            <option value="due">
              Outstanding Due
            </option>

            <option value="credit">
              Credit Balance
            </option>

            <option value="clear">
              Cleared
            </option>
          </select>

        </div>

      </div>

      {/* CUSTOMER TABLE */}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="hidden overflow-x-auto md:block">

          <table className="w-full">

            <thead className="bg-slate-900 text-white">

              <tr>
                <th className="px-4 py-4 text-left text-xs font-black uppercase">
                  Customer
                </th>

                <th className="px-4 py-4 text-left text-xs font-black uppercase">
                  Phone
                </th>

                <th className="px-4 py-4 text-right text-xs font-black uppercase">
                  Invoice
                </th>

                <th className="px-4 py-4 text-right text-xs font-black uppercase">
                  Paid
                </th>

                <th className="px-4 py-4 text-right text-xs font-black uppercase">
                  Credit
                </th>

                <th className="px-4 py-4 text-right text-xs font-black uppercase">
                  Outstanding
                </th>

                <th className="px-4 py-4 text-center text-xs font-black uppercase">
                  Action
                </th>
              </tr>

            </thead>

            <tbody>

              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="py-16 text-center"
                  >
                    <RefreshCw
                      size={30}
                      className="mx-auto animate-spin text-slate-400"
                    />

                    <p className="mt-3 font-bold text-slate-500">
                      Loading customers...
                    </p>
                  </td>
                </tr>
              ) : filteredCustomers.length ===
                0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="py-16 text-center"
                  >
                    <User
                      size={40}
                      className="mx-auto text-slate-300"
                    />

                    <p className="mt-3 font-bold text-slate-500">
                      No customers found
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCustomers.map(
                  (c) => {
                    const outstanding =
                      Number(
                        c.outstanding_balance ||
                          0
                      );

                    return (
                      <tr
                        key={
                          c.customer_id
                        }
                        className="border-t border-slate-100 hover:bg-slate-50"
                      >

                        <td className="px-4 py-4">

                          <div className="flex items-center gap-3">

                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 font-black text-slate-700">
                              {(
                                c.name ||
                                "C"
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <p className="font-black text-slate-800">
                                {
                                  c.name
                                }
                              </p>

                              <p className="text-xs text-slate-400">
                                ID #
                                {
                                  c.customer_id
                                }
                              </p>
                            </div>

                          </div>

                        </td>

                        <td className="px-4 py-4 text-sm font-semibold text-slate-600">
                          {c.phone ||
                            "-"}
                        </td>

                        <td className="px-4 py-4 text-right font-bold text-slate-700">
                          {money(
                            c.total_invoice_amount
                          )}
                        </td>

                        <td className="px-4 py-4 text-right font-bold text-emerald-600">
                          {money(
                            c.total_paid
                          )}
                        </td>

                        <td className="px-4 py-4 text-right font-bold text-orange-600">
                          {money(
                            c.total_credit_note
                          )}
                        </td>

                        <td className="px-4 py-4 text-right">

                          <span
                            className={`font-black ${
                              outstanding >
                              0
                                ? "text-red-600"
                                : outstanding <
                                  0
                                ? "text-emerald-600"
                                : "text-slate-600"
                            }`}
                          >
                            {money(
                              outstanding
                            )}
                          </span>

                        </td>

                        <td className="px-4 py-4 text-center">

                          <button
                            onClick={() =>
                              openCustomer(
                                c.customer_id
                              )
                            }
                            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white hover:bg-slate-700"
                          >
                            <Eye
                              size={16}
                            />
                            View
                          </button>

                        </td>

                      </tr>
                    );
                  }
                )
              )}

            </tbody>

          </table>

        </div>

        {/* MOBILE */}

        <div className="space-y-3 p-3 md:hidden">

          {loading ? (
            <div className="py-12 text-center">
              <RefreshCw
                size={30}
                className="mx-auto animate-spin text-slate-400"
              />

              <p className="mt-3 font-bold text-slate-500">
                Loading...
              </p>
            </div>
          ) : filteredCustomers.length ===
            0 ? (
            <div className="py-12 text-center">
              <User
                size={35}
                className="mx-auto text-slate-300"
              />

              <p className="mt-3 font-bold text-slate-500">
                No customers found
              </p>
            </div>
          ) : (
            filteredCustomers.map(
              (c) => {
                const outstanding =
                  Number(
                    c.outstanding_balance ||
                      0
                  );

                return (
                  <div
                    key={
                      c.customer_id
                    }
                    className="rounded-2xl border border-slate-200 p-4"
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div className="flex items-center gap-3">

                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 font-black text-white">
                          {(
                            c.name ||
                            "C"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>

                        <div>
                          <p className="font-black text-slate-900">
                            {
                              c.name
                            }
                          </p>

                          <p className="text-xs text-slate-500">
                            {c.phone ||
                              "No phone"}
                          </p>
                        </div>

                      </div>

                      <button
                        onClick={() =>
                          openCustomer(
                            c.customer_id
                          )
                        }
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white"
                      >
                        <ChevronRight
                          size={17}
                        />
                      </button>

                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">

                      <MobileStat
                        label="Invoice"
                        value={money(
                          c.total_invoice_amount
                        )}
                      />

                      <MobileStat
                        label="Paid"
                        value={money(
                          c.total_paid
                        )}
                      />

                      <MobileStat
                        label="Credit"
                        value={money(
                          c.total_credit_note
                        )}
                      />

                      <MobileStat
                        label="Outstanding"
                        value={money(
                          outstanding
                        )}
                        danger={
                          outstanding > 0
                        }
                      />

                    </div>

                  </div>
                );
              }
            )
          )}

        </div>

      </div>

      {/* MODALS */}

      <CustomerDrawer />

      <PaymentModal />

      <CreditModal />

      <CustomerModal />

      {/* DETAIL LOADING */}

      {loadingDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">

          <div className="rounded-2xl bg-white px-6 py-5 shadow-2xl">

            <RefreshCw
              size={28}
              className="mx-auto animate-spin text-slate-700"
            />

            <p className="mt-3 font-bold text-slate-700">
              Loading customer...
            </p>

          </div>

        </div>
      )}

    </div>
  );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function SummaryCard({
  title,
  value,
  icon,
  green,
  orange,
  red,
}) {
  let color =
    "bg-slate-100 text-slate-700";

  if (green) {
    color =
      "bg-emerald-100 text-emerald-700";
  }

  if (orange) {
    color =
      "bg-orange-100 text-orange-700";
  }

  if (red) {
    color =
      "bg-red-100 text-red-700";
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">

      <div className="flex items-center justify-between">

        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${color}`}
        >
          {icon}
        </div>

      </div>

      <p className="mt-3 text-xs font-bold uppercase tracking-wide text-slate-400">
        {title}
      </p>

      <p className="mt-1 truncate text-lg font-black text-slate-900">
        {value}
      </p>

    </div>
  );
}

/* =========================================================
   INFO ITEM
========================================================= */

function InfoItem({
  icon,
  label,
  value,
}) {
  return (
    <div className="flex items-start gap-3">

      <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
        {icon}
      </div>

      <div className="min-w-0">

        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
          {label}
        </p>

        <p className="mt-0.5 break-words text-sm font-bold text-slate-700">
          {value}
        </p>

      </div>

    </div>
  );
}

/* =========================================================
   MINI STAT
========================================================= */

function MiniStat({
  label,
  value,
}) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">

      <p className="text-xs font-bold text-slate-400">
        {label}
      </p>

      <p className="mt-1 text-lg font-black text-slate-900">
        {value}
      </p>

    </div>
  );
}

/* =========================================================
   MOBILE STAT
========================================================= */

function MobileStat({
  label,
  value,
  danger,
}) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">

      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <p
        className={`mt-1 text-sm font-black ${
          danger
            ? "text-red-600"
            : "text-slate-800"
        }`}
      >
        {value}
      </p>

    </div>
  );
}

/* =========================================================
   TAB BUTTON
========================================================= */

function TabButton({
  active,
  onClick,
  icon,
  label,
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black transition ${
        active
          ? "bg-slate-900 text-white shadow"
          : "text-slate-500 hover:bg-slate-100"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

/* =========================================================
   LEDGER ROW
========================================================= */

function LedgerRow({
  item,
  money,
}) {
  const debit =
    Number(item.debit || 0);

  const credit =
    Number(item.credit || 0);

  const isDebit = debit > 0;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-100 p-3 md:flex-row md:items-center md:justify-between">

      <div className="flex items-start gap-3">

        <div
          className={`flex h-9 w-9 items-center justify-center rounded-lg ${
            isDebit
              ? "bg-red-100 text-red-600"
              : "bg-emerald-100 text-emerald-600"
          }`}
        >
          {isDebit ? (
            <ArrowUpRight
              size={17}
            />
          ) : (
            <ArrowDownRight
              size={17}
            />
          )}
        </div>

        <div>

          <p className="font-bold text-slate-800">
            {item.transaction_type ||
              item.description ||
              "Transaction"}
          </p>

          <p className="text-xs text-slate-400">
            {item.reference_no ||
              "-"}
          </p>

          <p className="text-xs text-slate-400">
            {item.transaction_date ||
              "-"}
          </p>

        </div>

      </div>

      <div className="text-right">

        <p
          className={`font-black ${
            isDebit
              ? "text-red-600"
              : "text-emerald-600"
          }`}
        >
          {isDebit
            ? `+ ${money(debit)}`
            : `- ${money(credit)}`}
        </p>

        <p className="text-xs font-bold text-slate-400">
          Balance:{" "}
          {money(item.balance)}
        </p>

      </div>

    </div>
  );
}

/* =========================================================
   EMPTY
========================================================= */

function EmptyState({
  title,
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center">

      <FileText
        size={38}
        className="mx-auto text-slate-300"
      />

      <p className="mt-3 font-bold text-slate-500">
        {title}
      </p>

    </div>
  );
}

/* =========================================================
   MODAL
========================================================= */

function Modal({
  title,
  onClose,
  children,
}) {
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm md:items-center md:p-4">

      <div className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl md:rounded-3xl">

        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">

          <h2 className="text-lg font-black text-slate-900 md:text-xl">
            {title}
          </h2>

          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200"
          >
            <X size={18} />
          </button>

        </div>

        <div className="overflow-y-auto p-5">
          {children}
        </div>

      </div>

    </div>
  );
}

/* =========================================================
   INPUT
========================================================= */

function InputField({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  min,
  step,
}) {
  return (
    <div>

      <label className="mb-1.5 block text-sm font-bold text-slate-700">
        {label}
      </label>

      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        step={step}
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />

    </div>
  );
}

/* =========================================================
   SELECT
========================================================= */

function SelectField({
  label,
  name,
  value,
  onChange,
  children,
}) {
  return (
    <div>

      <label className="mb-1.5 block text-sm font-bold text-slate-700">
        {label}
      </label>

      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      >
        {children}
      </select>

    </div>
  );
}

/* =========================================================
   TEXTAREA
========================================================= */

function TextAreaField({
  label,
  name,
  value,
  onChange,
  placeholder,
}) {
  return (
    <div>

      <label className="mb-1.5 block text-sm font-bold text-slate-700">
        {label}
      </label>

      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />

    </div>
  );
}